// draftLogger.js — framework-agnostic auction-draft recorder.
//
// Produces a flat, JSON-safe event log of an auction draft. No React, no
// network, no DOM. The engine (batch sim or the live loop) calls the recorder
// methods; downstream consumers serialize/persist/analyze the result.
//
// Players are stored as {name, pos} ONLY. Per CLAUDE.md, draftLog player
// objects are JSON copies — holding UNIQUE_PLAYERS references is unsafe because
// resumed/serialized copies break === identity. Always match by name+pos.

// Serialize a player down to the only two fields we persist.
function toPlayerRef(player) {
  if (!player) return null;
  return { name: player.name, pos: player.pos };
}

/**
 * Create a draft-log recorder.
 *
 * @param {Object}  opts
 * @param {Object}  opts.settings   League settings snapshot (roster, scoring, teams…) — full absolute object, not deltas.
 * @param {number}  opts.userSeat   Seat index the human occupies (-1 for headless).
 * @param {Array}   opts.seats      [{seat, name, archetype, modifiers, favoriteTeam, overpay}]
 * @param {string}  opts.speed      Auction speed setting (affects bidding cadence).
 * @param {boolean} opts.autopick   Whether the user's seat was auto-drafted (flags non-human drafts).
 * @param {string}  opts.appVersion Engine build identifier (package version + optional commit SHA).
 * @param {boolean} opts.internal   Whether this is an internal (dev/owner) draft — labels it in notifications.
 * @param {string}  opts.devLabel   Optional human label for an internal draft.
 * @param {string}  opts.dials      Dev-only dial cell active for this draft (utils/devDials.js), else null.
 *                                  Present ONLY on drafts run with a probe cell activated, so those drafts
 *                                  can be excluded from analytics and hangdiag corpora rather than silently
 *                                  mixed with stock ones.
 * @param {number}  opts.viewport   Viewport width in px at draft start — the server cannot derive it. Null off-browser.
 * @param {string}  opts.leagueClass 'default' | 'saved' | 'edited' — how the league was set up. The name itself is never sent.
 * @param {number}  opts.keeperCount How many players were kept before pick 1. 0 for every non-keeper draft.
 * @param {number}  opts.keeperSpend Total dollars those keepers removed from the room's pot.
 *                                   COUNTS, NOT NAMES — see the note on the fields below.
 * @returns recorder with nominate/bid/dropout/sale/finalize methods.
 */
export function createDraftLog({ settings, userSeat, seats, speed, autopick, appVersion, internal, devLabel, dials, viewport, leagueClass, keeperCount, keeperSpend } = {}) {
  const state = {
    draftId:
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    startedAt: Date.now(),
    status: 'active',
    settings: settings || null,
    userSeat: typeof userSeat === 'number' ? userSeat : -1,
    seats: Array.isArray(seats) ? seats : [],
    speed: speed ?? null,
    autopick: !!autopick,
    appVersion: appVersion ?? null,
    internal: !!internal,
    devLabel: devLabel ?? null,
    // Non-null only when a dev dial cell was active — a stock draft's record
    // is byte-identical to before this field existed.
    dials: dials ?? null,
    // Client-only facts the server has no way to reconstruct from the log.
    viewport: typeof viewport === 'number' && Number.isFinite(viewport) ? viewport : null,
    leagueClass: leagueClass ?? null,
    // Keepers — LOAD-BEARING, not telemetry garnish. A keeper-ON room is a
    // different pricing regime (the pricing line measured room composition alone
    // moving a player's price ~3x), so every price metric computed after keepers
    // ship is quietly contaminated unless keeper drafts can be segmented OUT of
    // the corpus. These two fields are what makes that possible, and what makes
    // the acceptance harness's non-keeper-only scope enforceable rather than an
    // assumption. Same lesson as `appVersion` in the Aug 8 price ship.
    //
    // COUNTS, NOT NAMES, deliberately. The full record already persists every
    // sale, and the keeper rosters ride along in the team state — logging player
    // names and prices here would duplicate that while adding a second place for
    // league-identifying detail to leak.
    //
    // 0/0 on every non-keeper draft, which is the overwhelming majority, so a
    // stock record stays trivially comparable to one written before keepers.
    keeperCount: Number.isFinite(keeperCount) ? keeperCount : 0,
    keeperSpend: Number.isFinite(keeperSpend) ? keeperSpend : 0,
    events: [],
  };

  let seq = 0;

  // Append one event; returns the stored event for convenience.
  function push(type, args) {
    const event = { seq: seq++, type, ts: Date.now(), ...args };
    state.events.push(event);
    return event;
  }

  return {
    // Direct read access to the underlying log (JSON-safe).
    state,

    nominate({ pick, nominatorSeat, player, openingBid }) {
      return push('nominate', {
        pick,
        nominatorSeat,
        player: toPlayerRef(player),
        openingBid,
      });
    },

    bid({ pick, seat, isUser, amount }) {
      return push('bid', { pick, seat, isUser: !!isUser, amount });
    },

    dropout({ pick, seat }) {
      return push('dropout', { pick, seat });
    },

    sale({ pick, player, winnerSeat, price }) {
      return push('sale', {
        pick,
        player: toPlayerRef(player),
        winnerSeat,
        price,
      });
    },

    finalize(status) {
      state.status = status || 'complete';
      state.finishedAt = Date.now();
      return state;
    },
  };
}
